import * as React from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Alert,
  ModalFooter,
} from 'reactstrap';
import i18n from '@/utils/i18n';
import Checkbox from './Checkbox';
import {
  CheckBoxLabelContainer,
  CheckBoxLabel,
} from './Checkbox/pickpackCheckbox';

export type NewUserModalUserType =
  | 'partner_app'
  | 'warehouse_cleaning'
  | 'carrier_employee';

const USER_TYPE_PILL_KEY: Record<NewUserModalUserType, string> = {
  partner_app: 'Merchants:partnerAppUserPill',
  warehouse_cleaning: 'Merchants:warehouseCleaningUserPill',
  carrier_employee: 'Merchants:carrierEmployeeUserPill',
};

export interface NewUserInfo {
  username: string;
  password: string;
  isInternal: boolean;
  userType: NewUserModalUserType;
}

export interface NewUserModalProps {
  header: string;
  isOpen: boolean;
  errors: string[] | null;
  onCreateUser: (userInfo: NewUserInfo) => void;
  onClose: () => void;
  doNotAllowInternal?: boolean;
  allowedUserTypes?: NewUserModalUserType[];
}

export interface NewUserModalState {
  userInfo: NewUserInfo;
}

const DEFAULT_ALLOWED_USER_TYPES: NewUserModalUserType[] = ['partner_app'];

export class NewUserModal extends React.Component<
  NewUserModalProps,
  NewUserModalState
> {
  constructor(props: NewUserModalProps, state: NewUserModalState) {
    super(props, state);
    this.state = {
      userInfo: {
        username: '',
        password: '',
        isInternal: false,
        userType: (props.allowedUserTypes ?? DEFAULT_ALLOWED_USER_TYPES)[0],
      },
    };
  }

  render() {
    const allowedUserTypes =
      this.props.allowedUserTypes ?? DEFAULT_ALLOWED_USER_TYPES;
    const showUserTypeSelector = allowedUserTypes.length > 1;

    return (
      <Modal isOpen={this.props.isOpen} toggle={this.props.onClose}>
        <ModalHeader>{this.props.header}</ModalHeader>
        <ModalBody>
          {showUserTypeSelector && (
            <FormGroup>
              <Label>{i18n.t('Users:userType')}</Label>
              {allowedUserTypes.map((type) => (
                <FormGroup check key={type}>
                  <Label check>
                    <Input
                      type="radio"
                      name="userType"
                      checked={this.state.userInfo.userType === type}
                      onChange={() =>
                        this.setState({
                          userInfo: {
                            ...this.state.userInfo,
                            userType: type,
                          },
                        })
                      }
                    />{' '}
                    {i18n.t(USER_TYPE_PILL_KEY[type])}
                  </Label>
                </FormGroup>
              ))}
            </FormGroup>
          )}
          <FormGroup>
            <Label>{i18n.t('Users:username')}</Label>
            <Input
              autoComplete="new-password"
              type="text"
              placeholder={i18n.t('Users:vytalUser')}
              value={this.state.userInfo.username}
              onChange={(e) =>
                this.setState({
                  userInfo: {
                    ...this.state.userInfo,
                    username: e.target.value,
                  },
                })
              }
            />
            <Label>{i18n.t('Users:password')}</Label>
            <Input
              autoComplete="new-password"
              type="password"
              placeholder={i18n.t('Users:passwordPlaceholder')}
              value={this.state.userInfo.password}
              onChange={(e) =>
                this.setState({
                  userInfo: {
                    ...this.state.userInfo,
                    password: e.target.value,
                  },
                })
              }
            />
          </FormGroup>
          {!this.props.doNotAllowInternal && (
            <CheckBoxLabelContainer
              onClick={() =>
                this.setState({
                  userInfo: {
                    ...this.state.userInfo,
                    isInternal: !this.state.userInfo.isInternal,
                  },
                })
              }
            >
              <Checkbox checked={this.state.userInfo.isInternal} />
              <CheckBoxLabel>{i18n.t('Users:internalUser')}</CheckBoxLabel>
            </CheckBoxLabelContainer>
          )}

          {this.props.errors ? (
            <Alert color="danger">
              <ul style={{ marginBottom: 0 }}>
                {this.props.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            className="text-white"
            onClick={() => this.props.onCreateUser(this.state.userInfo)}
            disabled={
              !this.state.userInfo.username || !this.state.userInfo.password
            }
          >
            {i18n.t('Users:addUser')}
          </Button>
          <Button color="secondary" onClick={this.props.onClose}>
            {i18n.t('Common:close')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}
